import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  PushButton,
  VerticalProgress,
} from '../components';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import {
  Sound,
  Sounds,
} from '../../Sound';
import {
  TargetLabel,
  BalanceLabel,
  DeviceStatusLabel,
} from '../components/labels';
import logger from '../../../utils/logger';
import {
  toNumber,
  zeroPad,
} from '../../../utils/numbers';
import convertUnit from '../../../utils/unit-converter';
import { PumpAccumulatorDisplayType } from '../../../constants/Configuration';
import Stopwatch from '../../Stopwatch/Stopwatch';
import {
  getRunMode,
  isRunModeNewYork,
} from '../../../stores/batch/run-mode';
import InfoTooltip from '../../InfoTooltip';

@inject('batchStore', 'materialStore', 'settingsStore')
@observer
class PumpDevice extends Component {
  finishWaterPump() {
    const { device, batchStore } = this.props;
    const pumpStatus = batchStore.getDeviceStatus(device.id);
    const runningData = batchStore.running.find(v => v.currentDrop === pumpStatus.currentDrop && v.allDrops === pumpStatus.allDrops);

    if (runningData) {
      const pumpData = runningData.pumps[device.id];

      if (pumpData && !pumpData.washWaterDischarged) {
        const { material } = pumpData;

        pumpData.headWaterDischarged = true;
        pumpData.washWaterDischarged = true;

        runningData.headWaterPumpDischarged = true;
        runningData.washWaterPumpDischarged = true;

        batchStore.finishFeedDevices = batchStore.finishFeedDevices.filter(id => id !== device.id);
        batchStore.holdFeedDevices = batchStore.holdFeedDevices.filter(id => id !== device.id);

        if (runningData && runningData.mixDesign) {
          runningData.mixDesign.forEach((v, i) => {
            if ((v.batchCode && v.batchCode === material.batchCode) || v.constituentItemId === material.itemId) {
              runningData.mixDesign[i].actualWeight = pumpStatus.weight;
              logger.log('actual weight', material.code, pumpStatus.weight, runningData.mixDesign[i]);
            }
          });
        }

        batchStore.deviceStatus.set(device.id, {
          ...batchStore.getDeviceStatus(device.id),
          error: false,
          status: DeviceStatus.FINISH,
        });

        if (device.tankId) {
          batchStore.onFinishNonBottlePump(pumpData, runningData, runningData.batchActionFired[[...runningData.batchActionFired].pop()]);
        } else {
          pumpData.isDischarged = true;

          batchStore.resumePrimaryScaleDischarge(runningData);

          if (runningData.materialId) {
            if (runningData.mixerId) {
              batchStore.dischargeMixer(runningData);
            } else {
              batchStore.finishDischarge(runningData);
            }
          }
        }

      }

      logger.log(`pump ${device.description} manually finish feed`, pumpData);

    }
  }

  render() {
    const { device, batchStore, materialStore, settingsStore, onPush, onPushUp } = this.props;
    const { configuration } = settingsStore;
    const { materials, materialDetails } = materialStore;
    const active = batchStore.isOutputOn(device.gatePNo);
    const isEmpty = batchStore.isPumpEmpty(device);
    const deviceStatus = batchStore.getDeviceStatus(device.id);
    const { status, target, uid, truckCode, tId, currentDrop, allDrops, flowRate } = deviceStatus;
    const runMode = getRunMode(configuration, batchStore);
    const error = deviceStatus.error ? 'error' : '';
    const mCode = uid ? `${truckCode || tId || uid} ${currentDrop}/${allDrops}` : '';
    const material = materials.find(v => v.pumpId === device.id);
    const md = material ? materialDetails[material.materialDetailId] || {} : {};
    const unit = md.itemBatchUnitSymbol || 'ga';
    const weight = batchStore.getPumpWeight(device) * (md.meterRate || 1) || 0;
    const totalWeightInActiveLoad = batchStore.getTotalWeightInActiveLoad(device) || 0;
    const progress = deviceStatus.weight > 0 && (status !== DeviceStatus.FINISH && status !== DeviceStatus.IDLE)
      ? 100 - (((target - deviceStatus.weight) / target) * 100)
      : isEmpty ? 0 : 20;
    const tolerance = target ? batchStore.getToleranceByPercent(md, target) : 0;
    const materialName = material ? materialStore.getMaterialName(material) : null;
    const type = materialName || device.description;
    const inputDisabled = batchStore.isInputDisabled(device.emptyStatusPNo) ? 'device-input-disabled' : '';
    const isFeedMode = batchStore.isHoldFeedDevice(device.id) || [DeviceStatus.FEED, DeviceStatus.HOLD].includes(status);
    const holdFeedIcon = batchStore.isHoldFeedDevice(device.id) ? 'play' : 'pause';
    const deviceUnit = device.unitId ? batchStore.getUnitById(device.unitId) : null;
    const isDeferenceUnit = deviceUnit && unit ? unit !== deviceUnit : false;
    let convertedTarget;
    let convertedWeight;

    if (isDeferenceUnit) {
      convertedTarget = toNumber(convertUnit(target, deviceUnit, unit), 0);
      convertedWeight = toNumber(convertUnit(deviceStatus.weight, deviceUnit, unit), 0);
    }
    const flowRateTimeout = md.flowRateTimeout || 10;
    const canDisplayFlowRateCounter = flowRateTimeout && md.minimumFlowRate
      && batchStore.running.length && batchStore.canEmit && !batchStore.replayMode;

    // Use adjusted target from device status if available
    const targetToDisplay = deviceStatus.adjustedTargetForDisplay !== undefined ? deviceStatus.adjustedTargetForDisplay : target;

    return (
      <div id={`device-${device.id}`} className={`${active} status-${status} device-gui device-gui--pump device-${error} ${inputDisabled}`}>
        <Sound file={Sounds.alarm} play={error} loop={true}/>
        <DeviceStatusLabel
          name={type}
          status={status}
          description={device.description}
          title={`#${device.id} ${inputDisabled}`}
          displayIcon={inputDisabled}
        />
        {canDisplayFlowRateCounter && (
          <Stopwatch
            start={status === DeviceStatus.FEED}
            stop={flowRate > md.minimumFlowRate || status !== DeviceStatus.FEED}
            delay={flowRateTimeout}
            countdown={true}
            hideIcon={true}
          />
        )}
        {device.gatePNo && device.meterPNo && !!isFeedMode && !batchStore.isHoldFeed
        && !isRunModeNewYork(runMode) && (
          <button className={'btn-on btn-batch btn-batch-red'}
            title={batchStore.isHoldFeedDevice(device.id) ? 'Start feed' : 'Hold feed'}
            onClick={(e) => {
              if (!batchStore.canSendRemoteInstruction()) {
                e.stopPropagation();
              }
              batchStore.onPausePlayFeedButtonPress(device.id);
            }}>
            <i className={`fa fa-${holdFeedIcon}`}/>
          </button>
        )}
        {((!!isFeedMode && batchStore.isHoldFeedDevice(device.id)) || status === DeviceStatus.WAIT_WATER) && !isRunModeNewYork(runMode) && (
          <button className={'btn-on btn-batch btn-batch-red btn-finish-feed'}
            title="Finish feed"
            onClick={(e) => {
              e.stopPropagation();
              batchStore.onFinishFeedButtonPress(device.id, material, () => {
                this.finishWaterPump();
              });
            }}>
            <i className={'fa fa-stop'}/>
          </button>
        )}
        {(status === DeviceStatus.IDLE || status === DeviceStatus.FINISH)
        && material
        && !device.tankId
        && !!batchStore.running.length
        && !isRunModeNewYork(runMode)
        && (
          <button className={'btn-batch btn-batch-red btn-start-head-water'}
            title={'Start head water'}
            onClick={(e) => {
              e.stopPropagation();
              batchStore.startHeadWater(device);
            }}>
            <i className="fa fa-arrow-down"/>
          </button>
        )}
        {batchStore.startWashWaterButtonShouldBeVisible(device)
          && !isRunModeNewYork(runMode) && (
          <button className={'btn-batch btn-batch-red btn-start-wash-water'}
            title={'Start wash water'}
            onClick={(e) => {
              e.stopPropagation();
              batchStore.startWashWater(device);
            }}>
            <i className="fa fa-arrow-down"/>
          </button>
        )}
        <div className="device-icon body"/>
        <div className="device-icon water" style={{height: progress*0.47 + '%'}}/>
        <div className="device-label device-label-target">
          <TargetLabel
            target={targetToDisplay}
            unit={deviceUnit || unit}
            convertedTarget={isDeferenceUnit ? toNumber(convertUnit(targetToDisplay, deviceUnit, unit), 0) : convertedTarget}
            convertedUnit={unit}
            originalTarget={target}
            padZeros={md.meterRate > 1}
          />
          <BalanceLabel
            balance={toNumber(deviceStatus.weight, 2)}
            unit={deviceUnit || unit}
            convertedBalance={convertedWeight}
            convertedUnit={unit}
            padZeros={md.meterRate > 1}
          />
          {(settingsStore.configuration.pumpAccumulatorDisplayType === PumpAccumulatorDisplayType.ByLoad) ? (
            <>
              <div className="pac-balance">
                {md.meterRate > 1 ? zeroPad(totalWeightInActiveLoad, 2) : totalWeightInActiveLoad} {deviceUnit || unit}
                <InfoTooltip text={`Total ${materialName} pumped in current load including added water (if applicable)`} />
              </div>
            </>
          ): (
            <div className="pac-balance">
              {md.meterRate > 1 ? zeroPad(weight, 2) : toNumber(weight, 2)} {deviceUnit || unit}
              <InfoTooltip text={`Current meter reading - shows total ${materialName} pumped since last login or last counter clear event`} />
            </div>
          )}
          {mCode && <div className="mix" title={mCode}>{mCode}</div>}
        </div>

        {!configuration.manualFeed && batchStore.running.length ? '' : (
          <div className="device-buttons">
            {device.gatePNo && device.meterPNo && (
              <PushButton port={device.gatePNo}
                onPush={onPush}
                onPushUp={onPushUp}
                disabled={isRunModeNewYork(runMode)}
                onRightClickHoldLabel={'Hold to keep open..'}
                onRightClickHoldEnd={onPush}
                rightClickHoldDuration={1250}
              >
                <i className="fa fa-arrow-up"/>
              </PushButton>
            )}
            {device.meterPNo && !batchStore.running.length && (
              <button className="btn-batch btn-batch-red btn-reset-meter"
                title="Reset"
                onClick={() => {
                  batchStore.resetMeter(device.meterPNo);
                  batchStore.resetCounter(device.meterPNo);
                }}>
                <i className="fa fa-refresh"/>
              </button>
            )}
          </div>
        )}

        <VerticalProgress
          showPercent={true}
          tolerance={tolerance}
          value={target > 0 ? (deviceStatus.weight / target) * 100 : 0}
          active={active}
        />
      </div>
    );
  }
}

export default PumpDevice;
